import { ChangeEvent } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Add, DataImportHub, EventSchedule } from '@mint-lib/icons';
import {
  Button,
  Divider,
  ToggleGroup,
  ToggleGroupItem,
  typographyVariants,
} from '@mint-lib/tw-components';
import { SearchBar } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import { useAiHubPermissions } from '../hooks/useAiHubPermissions.jsx';

type Props = {
  searchValue: string;
  onChangeSearch: (e: ChangeEvent) => void;
  onClearSearch: () => void;
  isNewChat: boolean;
  onStartNewChat: () => void;
  selectedChatType: 'live' | 'scheduled' | 'all';
  onChangeChatType: (value: 'live' | 'scheduled' | 'all') => void;
  onToggleKnowledgeBase: () => void;
  onToggleScheduleList: () => void;
  autoFocusSearch?: boolean;
};

export const ChatSidebarToolbar = ({
  searchValue,
  onChangeSearch,
  onClearSearch,
  isNewChat,
  onStartNewChat,
  selectedChatType,
  onChangeChatType,
  onToggleKnowledgeBase,
  onToggleScheduleList,
  autoFocusSearch,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const { canScheduleChat } = useAiHubPermissions();

  return (
    <>
      <SearchBar
        onChange={onChangeSearch}
        onClear={onClearSearch}
        size="medium"
        placeholder={t('Search your chat')}
        value={searchValue}
        autoFocus={autoFocusSearch}
      />
      <Button
        onClick={onStartNewChat}
        aria-label="Add new chat"
        className="w-full whitespace-nowrap"
        disabled={isNewChat}
        size="large"
        variant={isNewChat ? 'outlined' : 'contained'}
      >
        <Add />
        <p
          className={typographyVariants({
            variant: 'bodyShort02',
          })}
        >
          {t('New chat')}
        </p>
      </Button>
      <Button
        variant="text"
        className="w-full justify-start"
        onClick={onToggleKnowledgeBase}
      >
        <DataImportHub />
        <p
          className={cn(
            typographyVariants({
              variant: 'bodyShort02',
            }),
            'text-start',
          )}
        >
          {t('Knowledge base')}
        </p>
      </Button>
      <Button
        variant="text"
        className="w-full justify-start"
        onClick={onToggleScheduleList}
        disabled={!canScheduleChat}
      >
        <EventSchedule />
        <p
          className={cn(
            typographyVariants({
              variant: 'bodyShort02',
            }),
            'text-start',
          )}
        >
          {t('Scheduled chat')}
        </p>
      </Button>
      <Divider className="mt-3" />
      {canScheduleChat && (
        <ToggleGroup
          value={selectedChatType}
          onValueChange={(value: 'live' | 'scheduled' | 'all') => {
            //FYI: Radix ToggleGroup deselects on second click, so value='' can broke
            if (!value) return;

            onChangeChatType(value);
          }}
          className="self-start"
          type="single"
        >
          <ToggleGroupItem value="all">{t('All')}</ToggleGroupItem>
          <ToggleGroupItem value="live">{t('Chat')}</ToggleGroupItem>
          <ToggleGroupItem value="scheduled">{t('Scheduled')}</ToggleGroupItem>
        </ToggleGroup>
      )}
    </>
  );
};
